import axios from 'axios';
import { createContext, useCallback, useEffect, useMemo, useState } from 'react';
import { authLogin, getEelj, loggedUserInfo, updateToken, userInfo } from '../app/api';
import { useNavigate } from 'react-router-dom';

export type UserBio = {
  id?: string,
  role?: string,
  lastName?: string,
  firstName?: string,
  registerNumber?: string,
  gender?: string,
  birthDate?: Date,
  address?: string,
  school?: string,
  grade?: string,
  parentPhone?: string,
  email?: string,
  phone?: string,
  image?: string,
}

export type UserContextProps = {
  id: string | undefined;
  setId: (id: string | undefined) => void;
  role: string | undefined,
  setRole: (role: string | undefined) => void,
  userBio: UserBio | undefined;
  setUserBio: (bio: any) => void;
  userOrders : any[] | undefined;
  setUserOrders: (orders: any[]) => void;
  eeljuud: any;
  setEeljuud: (eeljuud: any) => void;
  handleLogin: (username: string, password: string) => Promise<boolean>;
  handleLogout: () => void;
  fetchEeljuud: () => void;
  fetchLoggedUserData: () => void;
};

export const UserContext = createContext<UserContextProps>({
  id: undefined,
  setId: () => '',
  role: undefined,
  setRole: () => '',
  userBio: {},
  setUserBio: () => {},
  userOrders: [],
  setUserOrders: () => {},
  eeljuud: {activeEelj: [], eelj: []},
  setEeljuud: () => {},
  handleLogin: () => new Promise((resolve) => {resolve(true)}),
  handleLogout: async () => {},
  fetchEeljuud: async () => {},
  fetchLoggedUserData: async () => {},
});

export const UserProvider = (props: any) => {
  const [id, setId] = useState<string | undefined>(undefined);
  const [role, setRole] = useState<string | undefined>(undefined);
  const [userBio, setUserBio] = useState<UserBio | undefined>(undefined);
  const [userOrders, setUserOrders] = useState<any[] | undefined>(undefined);
  const [eeljuud, setEeljuud] = useState<any>({activeEelj: [], eelj: []});
  const token = localStorage.getItem('token');
  const navigate = useNavigate();

  const fetchLoggedUserData = useCallback(async () => {
    try {
      updateToken();
      const {data} = await loggedUserInfo();
      if(data) {
        setUserBio(data);
        setId(data.id);
        setRole(data.role);
      } else {
        handleLogout();
        return;
      }
  
      const userDataResp = await userInfo(data.id);
      
      setUserOrders(userDataResp.data.order);
    } catch(err: any) {
      console.log(err);
      if(err?.response?.status === 401) {
        handleLogout();
      }
    }
  }, []);
  
  const fetchEeljuud = useCallback(async () => {
    try {
      const {data} = await getEelj();
      setEeljuud(data);
    } catch(err) {
    }
  }, []);

  useEffect(() => {
    if(token) {
      updateToken();

      try {
        fetchLoggedUserData();
      } catch (err: any){
        if(err?.response?.status === 401) {
          setUserBio(undefined);
          handleLogout();
        }
      }
    }

    try {
      fetchEeljuud();
    } catch(err) {
    }
  }, []);

  const handleLogin = async (username: string, password: string) => {
    return new Promise<boolean>(async (resolve, reject) => {
      try {
        const resp = await authLogin({
          username,
          password
        });


        localStorage.setItem('token', resp.headers['token']);


        axios.defaults.headers.common['Authorization'] = resp.headers['token'];
        updateToken();
        fetchLoggedUserData();

        resolve(true);
      } catch(err) {
        reject('Error');
      }
    });
  };

  const handleLogout = () => {
    if(localStorage.getItem('token')) {
      setUserBio(undefined);
      setUserOrders(undefined);
      setRole(undefined);
      setId(undefined);
      localStorage.clear();
      navigate('/',{replace: true});
      axios.defaults.headers.common['Authorization'] = undefined;
    }
  };

  const pack = useMemo<UserContextProps>(() => ({
    id: id,
    setId: setId,
    role: role,
    setRole: setRole,
    userBio: userBio,
    setUserBio: setUserBio,
    userOrders: userOrders,
    setUserOrders: setUserOrders,
    eeljuud: eeljuud,
    setEeljuud: setEeljuud,
    handleLogin: handleLogin,
    handleLogout: handleLogout,
    fetchEeljuud: fetchEeljuud,
    fetchLoggedUserData: fetchLoggedUserData,
  }), [id, setId, role, setRole, userBio, setUserBio, eeljuud, setEeljuud, userOrders, setUserOrders, fetchEeljuud, fetchLoggedUserData]);

  return (
    <UserContext.Provider value={pack}>
      {props.children}
    </UserContext.Provider>
  )
}

export default UserProvider;